import { Pencil, Trash2, Power, PowerOff, ChevronDown, ChevronUp, Image as ImageIcon, FileText, Link2, Code } from 'lucide-react';
import type { ContenidoPublico, ContenidoPublicoGrouped } from '../../types/contenido-publico';
import { getSectionTitle, getFieldMeta } from './field-labels';

interface ContenidoPublicoListProps {
  pagina: string;
  content: ContenidoPublicoGrouped;
  onEdit: (item: ContenidoPublico) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  openSections: Record<string, boolean>;
  onToggleSection: (seccion: string) => void;
  expandAll?: boolean;
}

const tipoIcons: Record<string, React.ReactNode> = {
  TEXTO: <FileText className="w-3.5 h-3.5" />,
  TEXTO_LARGO: <FileText className="w-3.5 h-3.5" />,
  IMAGEN: <ImageIcon className="w-3.5 h-3.5" />,
  ENLACE: <Link2 className="w-3.5 h-3.5" />,
  JSON: <Code className="w-3.5 h-3.5" />,
};

const tipoLabels: Record<string, string> = {
  TEXTO: 'Texto',
  TEXTO_LARGO: 'Texto largo',
  IMAGEN: 'Imagen',
  ENLACE: 'Enlace',
  JSON: 'Lista / JSON',
};

function ValuePreview({ item }: { item: ContenidoPublico }) {
  if (item.tipo === 'IMAGEN') {
    if (!item.valor) return <span className="text-gray-400 italic text-xs">Sin imagen</span>;
    const base = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:3000';
    return (
      <img
        src={`${base}${item.valor}`}
        alt={item.clave}
        className="max-h-20 rounded border border-gray-200 object-cover"
      />
    );
  }

  if (item.tipo === 'JSON') {
    try {
      const parsed = JSON.parse(item.valor);
      const count = Array.isArray(parsed) ? parsed.length : 0;
      return (
        <div className="text-xs text-gray-600">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 font-medium">
            {count} {count === 1 ? 'elemento' : 'elementos'}
          </span>
          <p className="mt-1 text-xs text-gray-500 truncate max-w-md">
            {typeof parsed[0] === 'string' ? parsed[0] : JSON.stringify(parsed[0]).slice(0, 80)}...
          </p>
        </div>
      );
    } catch {
      return <span className="text-red-500 text-xs">JSON inválido</span>;
    }
  }

  return (
    <p className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-3">{item.valor}</p>
  );
}

export const ContenidoPublicoList: React.FC<ContenidoPublicoListProps> = ({
  pagina,
  content,
  onEdit,
  onDelete,
  onToggle,
  openSections,
  onToggleSection,
  expandAll = false,
}) => {
  const secciones = Object.keys(content);

  if (secciones.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">No hay contenido configurado para esta página.</p>
        <p className="text-xs text-gray-400 mt-1">Crea tu primer elemento con el botón superior.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {secciones.map((seccion) => {
        const items = content[seccion].sort((a, b) => a.orden - b.orden || a.clave.localeCompare(b.clave));
        const isOpen = expandAll || (openSections[seccion] ?? false);

        return (
          <div
            key={seccion}
            id={`cms-section-${seccion}`}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm scroll-mt-4"
          >
            <button
              onClick={() => onToggleSection(seccion)}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div>
                  <h3 className="text-xs font-medium text-gray-800">{getSectionTitle(seccion)}</h3>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Sección: <span className="font-mono">{seccion}</span>
                  </p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold shrink-0">
                  {items.length} {items.length === 1 ? 'campo' : 'campos'}
                </span>
              </div>
              {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>

            {isOpen && (
              <div className="divide-y divide-gray-100">
                {items.map((item) => {
                  const meta = getFieldMeta(pagina, seccion, item.clave);
                  return (
                    <div
                      key={item.id}
                      className={`px-6 py-5 ${!item.activo ? 'opacity-60 bg-gray-50' : ''}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                              {tipoIcons[item.tipo]}
                              {tipoLabels[item.tipo]}
                            </span>
                            <span className="text-xs font-medium text-gray-900">{meta.label}</span>
                            {!item.activo && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                                Inactivo
                              </span>
                            )}
                          </div>
                          {meta.hint && (
                            <p className="text-xs text-gray-500 mb-2">{meta.hint}</p>
                          )}
                          <div className="mt-1">
                            <ValuePreview item={item} />
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => onToggle(item.id)}
                            title={item.activo ? 'Desactivar' : 'Activar'}
                            className={`p-2 rounded-lg transition-colors ${
                              item.activo
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                          >
                            {item.activo ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => onEdit(item)}
                            title="Editar"
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDelete(item.id)}
                            title="Eliminar"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
